import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ArrowLeft, Save, Mail, Trash2, ChevronRight,
  Shield, Eye, Settings, BookOpen, GraduationCap, Lock, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMISSION_SECTIONS = [
  {
    key: 'acces_administrateur',
    label: 'Accès administrateur',
    icon: Shield,
    permissions: [
      { key: 'configuration_compte', label: 'Configuration du compte' },
      { key: 'suivi_commercial', label: 'Suivi commercial / création de devis' },
      { key: 'base_donnees_intervenants', label: 'Base de données des intervenants' },
      { key: 'tableau_bord_rapports', label: "Tableau de bord / Rapports d'activité" },
      { key: 'gestion_sessions', label: 'Gestion des sessions' },
    ]
  },
  {
    key: 'acces_etendus',
    label: 'Accès étendus',
    icon: Eye,
    permissions: [
      { key: 'base_donnees_clients', label: 'Base de données clients (apprenants / entreprises / financeurs / salles)' },
      { key: 'modification_champs_personnalises', label: 'Modification des champs personnalisés' },
      { key: 'bibliotheque', label: "Bibliothèque (Programmes / Séquences e-learning / Activités e-learning / Évaluations)" },
      { key: 'notes_taches', label: 'Notes et tâches' },
      { key: 'voir_taches_autres', label: 'Voir les tâches concernant les autres' },
    ]
  },
  {
    key: 'visibilite_sessions',
    label: 'Visibilité des sessions / opportunités',
    icon: Eye,
    permissions: [
      { key: 'creer_sessions', label: 'Créer des sessions' },
      { key: 'voir_sessions_autres', label: 'Voir les sessions concernant les autres' },
      { key: 'voir_opportunites_autres', label: 'Voir les opportunités concernant les autres' },
    ]
  },
  {
    key: 'fonctions_admin_sessions',
    label: 'Fonctions administratives dans les sessions',
    icon: Settings,
    permissions: [
      { key: 'parametrage_clients_tarifs', label: 'Paramétrage des clients et tarifs' },
      { key: 'parametrage_programme', label: 'Paramétrage du programme' },
      { key: 'parametrage_intervenants', label: 'Paramétrage des intervenants' },
      { key: 'parametrage_lieu_dates', label: 'Paramétrage du lieu et des dates' },
      { key: 'envoi_convocations', label: 'Envoi des convocations' },
      { key: 'suivi_apprenants', label: 'Suivi des apprenants' },
      { key: 'rapport_qualite_session', label: 'Rapport Qualité de la session' },
      { key: 'page_finance_session', label: 'Page Finance de la session' },
      { key: 'envoi_emails', label: "Envoi d'emails" },
      { key: 'acces_url_personnalisees', label: 'Accès aux URL personnalisées des apprenants' },
    ]
  },
  {
    key: 'fonctions_pedagogiques_sessions',
    label: 'Fonctions pédagogiques dans les sessions',
    icon: GraduationCap,
    permissions: [
      { key: 'configuration_extranet', label: "Configuration de l'extranet" },
      { key: 'sequences_elearning', label: 'Séquences e-learning' },
      { key: 'emargements', label: 'Émargements' },
      { key: 'evaluations_session', label: 'Évaluations de la session' },
    ]
  },
];

const CompteAccesDetailPage = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const isNew = !accountId || accountId === 'nouveau';

  const [form, setForm] = useState({ prenom: '', nom: '', email: '', type: 'Administrateur' });
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  useEffect(() => {
    if (!isNew) {
      fetchAccount();
      fetchPasswordStatus();
    }
  }, [accountId]);

  const fetchAccount = async () => {
    try {
      const res = await fetch(`${API}/config/comptes-acces/${accountId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setForm({ prenom: data.prenom || '', nom: data.nom || '', email: data.email || '', type: data.type || 'Administrateur' });
        setPermissions(data.permissions || {});
      } else {
        toast.error("Compte non trouvé");
        navigate('/configuration/comptes-acces');
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchPasswordStatus = async () => {
    try {
      const res = await fetch(`${API}/config/comptes-acces/${accountId}/has-password`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.clone().json();
        setHasPassword(data.has_password);
      }
    } catch (e) { console.error(e); }
  };

  const handleSetPassword = async () => {
    if (newPassword.length < 8) { toast.error("Le mot de passe doit contenir au moins 8 caractères"); return; }
    try {
      const res = await fetch(`${API}/config/comptes-acces/${accountId}/password`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ password: newPassword })
      });
      if (res.ok) {
        toast.success("Mot de passe défini avec succès");
        setNewPassword(''); setShowPasswordForm(false); setHasPassword(true);
      } else {
        let msg = "Erreur"; try { const err = await res.clone().json(); msg = err.detail || msg; } catch {} toast.error(msg);
      }
    } catch (e) { toast.error("Erreur de connexion"); }
  };

  const togglePermission = (sectionKey, permKey) => {
    setPermissions(prev => ({
      ...prev,
      [sectionKey]: {
        ...(prev[sectionKey] || {}),
        [permKey]: !(prev[sectionKey]?.[permKey] || false),
      }
    }));
  };

  const handleSave = async () => {
    if (!form.email) { toast.error("L'email est requis"); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/config/comptes-acces` : `${API}/config/comptes-acces/${accountId}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...form, permissions })
      });
      if (res.ok) {
        toast.success(isNew ? "Compte créé" : "Compte mis à jour");
        if (isNew) {
          const data = await res.json();
          navigate(`/configuration/comptes-acces/${data.account_id}`);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
      }
    } catch (e) { toast.error("Erreur de sauvegarde"); }
    finally { setSaving(false); }
  };

  const handleInvite = async () => {
    if (isNew) return;
    try {
      const res = await fetch(`${API}/config/comptes-acces/invite`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ account_ids: [accountId] })
      });
      if (res.ok) toast.success("Invitation envoyée");
    } catch (e) { toast.error("Erreur d'envoi"); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer définitivement ce compte d'accès ?")) return;
    try {
      const res = await fetch(`${API}/config/comptes-acces/${accountId}`, {
        method: 'DELETE', credentials: 'include'
      });
      if (res.ok) { toast.success("Compte supprimé"); navigate('/configuration/comptes-acces'); }
    } catch (e) { toast.error("Erreur"); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin h-8 w-8 border-4 border-violet-500 border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="compte-detail-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <button onClick={() => navigate('/configuration/comptes-acces')} className="hover:text-slate-600">
            Configuration
          </button>
          <ChevronRight className="h-3.5 w-3.5" />
          <button onClick={() => navigate('/configuration/comptes-acces')} className="hover:text-slate-600">
            Comptes d'accès
          </button>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-600 font-medium">{isNew ? 'Nouveau' : `${form.nom} ${form.prenom}`}</span>
        </div>

        {/* Header with actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/configuration/comptes-acces')}
              className="text-slate-400 hover:text-slate-600" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-black text-slate-900" data-testid="detail-title">
              {isNew ? "Nouvel accès" : `${form.nom} ${form.prenom}`}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleSave} disabled={saving} data-testid="save-top-btn">
              <Save className="h-4 w-4 mr-2" /> Enregistrer
            </Button>
            {!isNew && (
              <>
                <Button variant="outline" onClick={handleInvite} data-testid="invite-btn">
                  <Mail className="h-4 w-4 mr-2" /> Inviter
                </Button>
                <Button variant="ghost" size="icon" className="text-slate-400 hover:text-red-500"
                  onClick={handleDelete} data-testid="delete-btn">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        {/* User info form */}
        <Card className="border-0 shadow-sm" data-testid="user-info-card">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="prenom" className="text-sm font-medium text-slate-600">Prénom</Label>
                <Input id="prenom" value={form.prenom}
                  onChange={e => setForm(p => ({ ...p, prenom: e.target.value }))}
                  className="mt-1" data-testid="detail-prenom" />
              </div>
              <div>
                <Label htmlFor="nom" className="text-sm font-medium text-slate-600">Nom</Label>
                <Input id="nom" value={form.nom}
                  onChange={e => setForm(p => ({ ...p, nom: e.target.value }))}
                  className="mt-1" data-testid="detail-nom" />
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-slate-600">Email</Label>
                <Input id="email" type="email" value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  className="mt-1" data-testid="detail-email" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Password section */}
        {!isNew && (
          <Card className="border-0 shadow-sm" data-testid="password-section">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2.5 mb-4">
                <KeyRound className="h-5 w-5 text-violet-500" />
                <h3 className="text-base font-bold text-slate-800">Mot de passe</h3>
              </div>
              <div className="pl-7">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${hasPassword ? 'bg-green-500' : 'bg-red-400'}`} />
                  <span className="text-sm text-slate-600">
                    {hasPassword ? 'Mot de passe défini' : 'Aucun mot de passe défini — connexion impossible'}
                  </span>
                </div>
                {showPasswordForm ? (
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label htmlFor="new-pwd" className="text-sm text-slate-500">Nouveau mot de passe (min. 8 caractères)</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input id="new-pwd" type="password" value={newPassword}
                          onChange={e => setNewPassword(e.target.value)} className="pl-10"
                          placeholder="Minimum 8 caractères" data-testid="new-password-input" />
                      </div>
                    </div>
                    <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleSetPassword}
                      disabled={newPassword.length < 8} data-testid="set-password-btn">
                      Enregistrer
                    </Button>
                    <Button variant="outline" onClick={() => { setShowPasswordForm(false); setNewPassword(''); }}>
                      Annuler
                    </Button>
                  </div>
                ) : (
                  <Button variant="outline" className="text-violet-600 border-violet-200"
                    onClick={() => setShowPasswordForm(true)} data-testid="change-password-btn">
                    <KeyRound className="h-4 w-4 mr-2" />
                    {hasPassword ? 'Changer le mot de passe' : 'Définir un mot de passe'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Permissions sections */}
        {PERMISSION_SECTIONS.map(section => {
          const Icon = section.icon;
          return (
            <Card key={section.key} className="border-0 shadow-sm" data-testid={`perm-section-${section.key}`}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2.5 mb-4">
                  <Icon className="h-5 w-5 text-violet-500" />
                  <h3 className="text-base font-bold text-slate-800">{section.label}</h3>
                </div>
                <div className="space-y-3 pl-7">
                  {section.permissions.map(perm => {
                    const checked = permissions[section.key]?.[perm.key] || false;
                    return (
                      <label key={perm.key} className="flex items-center gap-3 cursor-pointer group">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => togglePermission(section.key, perm.key)}
                          className="data-[state=checked]:bg-violet-600 data-[state=checked]:border-violet-600"
                          data-testid={`perm-${section.key}-${perm.key}`}
                        />
                        <span className={`text-sm transition-colors ${checked ? 'text-slate-800 font-medium' : 'text-slate-500 group-hover:text-slate-700'}`}>
                          {perm.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Bottom save button */}
        <div className="flex justify-end pb-8">
          <Button className="bg-violet-600 hover:bg-violet-700 text-white px-8"
            onClick={handleSave} disabled={saving} data-testid="save-bottom-btn">
            <Save className="h-4 w-4 mr-2" /> Enregistrer
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default CompteAccesDetailPage;
